import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { IconRefresh } from "@tabler/icons-react";

import MetaLogo from "@/assets/meta-logo.svg?react";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import { UnderlineTabs } from "@/components/ui/underline-tabs";
import { PeriodToggle } from "@/components/dashboard/period-toggle";
import { getMetaAdsStatus } from "@/services/metaAds.service";
import { refreshPerformance, type CurrencyView } from "@/services/metaPerformance.service";
import { cn } from "@/lib/utils";

import { errorMessage } from "../../tiktok/tiktokFormat";
import { OverviewTab } from "./OverviewTab";
import { CampaignsTab } from "./CampaignsTab";
import { CreatorsTab } from "./CreatorsTab";
import { RulesTab } from "./RulesTab";
import { AdDetailSheet } from "./AdDetailSheet";

type Tab = "overview" | "campaigns" | "creators" | "rules";
const TABS: Array<{ value: Tab; label: string }> = [
  { value: "overview", label: "Overview" },
  { value: "campaigns", label: "Campaigns" },
  { value: "creators", label: "Creators" },
  { value: "rules", label: "Rules" },
];
const readTab = (v: string | null): Tab => (TABS.some((t) => t.value === v) ? (v as Tab) : "overview");
const readDays = (v: string | null): number => ([7, 30, 90].includes(Number(v)) ? Number(v) : 30);

/** What every tab needs: the window, the filter, and the ad-sheet opener. */
export interface PerfScope {
  days: number;
  reveshareOnly: boolean;
  openAd: (metaAdId: string) => void;
  openCampaign: (campaignId: string) => void;
}

/**
 * Ad Performance: what the brand spends on Meta, what comes back, and which
 * creators that money comes from. Meta's figures lead; Reveshare's
 * attributed sales sit beside them. Nothing is converted across currencies.
 */
export default function AdPerformancePage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const tab = readTab(params.get("tab"));
  const days = readDays(params.get("days"));
  const reveshareOnly = params.get("mine") === "1";
  const openAdId = params.get("ad");

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v === null) next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const status = useQuery({ queryKey: ["meta-ads-status"], queryFn: getMetaAdsStatus });
  const connected = !!status.data?.connected && !status.data.deauthorized;
  const accountId = status.data?.defaultAdAccountId ?? null;
  const ready = connected && !!accountId;

  const [currency, setCurrency] = useState<CurrencyView | null>(null);

  const refresh = useMutation({
    mutationFn: () => refreshPerformance(days),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["meta-performance"] });
      toast.success("Pulled the latest figures from Meta");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const scope = useMemo<PerfScope>(
    () => ({
      days,
      reveshareOnly,
      openAd: (id) => update({ ad: id }),
      openCampaign: (id) => update({ tab: "campaigns", campaign: id }),
    }),
    [days, reveshareOnly, update],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ad Performance"
        description="Spend, returns and the creators behind them, from Meta and from Reveshare's own attribution"
        customActions={
          ready
            ? [
                {
                  id: "refresh",
                  label: refresh.isPending ? "Refreshing" : "Refresh from Meta",
                  icon: <IconRefresh size={16} className={cn(refresh.isPending && "animate-spin")} />,
                  onClick: () => refresh.mutate(),
                },
              ]
            : []
        }
      />

      {status.isLoading ? (
        <div className="h-40 animate-pulse rounded-lg border border-border bg-card" />
      ) : !ready ? (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            bordered={false}
            icon={<MetaLogo className="size-5" />}
            title={connected ? "Choose a default ad account" : "Connect your Meta ad account"}
            body={
              connected
                ? "Pick the ad account to read on the Meta Ads page and this page fills in"
                : "Once Meta is connected, spend, ROAS and creator performance show up here"
            }
            action={
              <Button asChild>
                <Link to="/brand/meta-ads">{connected ? "Open Meta Ads settings" : "Connect Meta"}</Link>
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
            <UnderlineTabs tabs={TABS} value={tab} onValueChange={(v) => update({ tab: v })} accent="brand" />
            <div className="ml-auto flex flex-wrap items-center gap-3">
              {currency && <CurrencyChip currency={currency} />}
              <label htmlFor="perf-mine" className="flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground">
                <Switch id="perf-mine" checked={reveshareOnly} onCheckedChange={(v) => update({ mine: v ? "1" : null })} />
                Reveshare-launched only
              </label>
              <PeriodToggle value={days} onChange={(d) => update({ days: String(d) })} />
            </div>
          </div>

          {tab === "overview" && <OverviewTab scope={scope} onCurrency={setCurrency} />}
          {tab === "campaigns" && (
            <CampaignsTab scope={scope} onCurrency={setCurrency} focusCampaignId={params.get("campaign")} />
          )}
          {tab === "creators" && <CreatorsTab scope={scope} onCurrency={setCurrency} />}
          {tab === "rules" && <RulesTab scope={scope} />}

          <AdDetailSheet metaAdId={openAdId} days={days} onClose={() => update({ ad: null })} />
        </>
      )}
    </div>
  );
}

/** Which currency the Meta figures are in, and whether the store disagrees. */
function CurrencyChip({ currency }: { currency: CurrencyView }) {
  if (currency.conflict) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-400"
        title={`Ads are billed in ${currency.account}, your store sells in ${currency.store}. Nothing is converted; figures across the two are not computed.`}
      >
        <span aria-hidden className="size-1.5 rounded-full bg-amber-500" />
        Currency conflict: ads {currency.account}, store {currency.store}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground" title="Every Meta figure on this page is in this currency">
      Ads in {currency.account}
    </span>
  );
}
